import { defineStore } from 'pinia'
import { doc, updateDoc } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { kebabCase, sortBy } from 'lodash-es'
import { useAppStore } from '@/stores/appStore'
import { useAnalysisStore } from '@/stores/analysisStore'
import { useAnalysisTestStore } from '@/stores/analysisTestStore'
import { useSampleAreaStore } from '@/stores/sampleAreaStore'
import { useTurnaroundTimeStore } from '@/stores/turnaroundTimeStore'
import { useAddonServiceStore } from '@/stores/addonServiceStore'
import { useTextListStore } from '@/stores/textListStore'
import { useLabEquipmentStore } from '@/stores/labEquipmentStore'
import { usePricingSegmentStore } from '@/stores/pricingSegmentStore'
import { useDropPointStore } from '@/stores/dropPointStore'
import { useClosedDayStore } from '@/stores/closedDayStore'
import { useReportDescriptionStore } from '@/stores/reportDescriptionStore'
import { AnalysisTest } from '@/globals/typescript/models/AnalysisTest'

type TranslationRow = {
  id: string
  rowKey: string
  label?: string
  location?: string
  fieldPath: string
  subId?: string
  textObj: Record<string, string>
}

export function buildSlugMap(
  tests: AnalysisTest[],
  locale: string
): Record<string, string> {
  const seen: Record<string, number> = {}
  return Object.fromEntries(
    tests.map((test) => {
      const name = test.name[locale] || test.name['en'] || ''
      const base = kebabCase(name)
      seen[base] = (seen[base] ?? 0) + 1
      const slug = seen[base] === 1 ? base : `${base}-${seen[base]}`
      return [test.id, slug]
    })
  )
}

export function buildTestRows(
  test: AnalysisTest,
  slug: string
): TranslationRow[] {
  const rows: TranslationRow[] = [
    {
      id: test.id,
      rowKey: `${test.id}:name`,
      location: `${slug}.name`,
      fieldPath: 'name',
      textObj: test.name,
    },
    {
      id: test.id,
      rowKey: `${test.id}:displayName`,
      location: `${slug}.display-name`,
      fieldPath: 'displayName',
      textObj: test.displayName ?? {},
    },
  ]

  if (test.type === 'single-select' || test.type === 'multi-select-text') {
    const activeAnswers = sortBy(
      test.answers.filter((a) => a.activeState === 'active'),
      'createdAt'
    )
    activeAnswers.forEach((answer, i) => {
      rows.push({
        id: test.id,
        rowKey: `${test.id}:${answer.id}`,
        location: `${slug}.answer-${i + 1}`,
        fieldPath: 'answers',
        subId: answer.id,
        textObj: answer.text,
      })
    })
  } else if (test.type === 'multi-select-number') {
    test.answerOverrides.forEach((override, i) => {
      rows.push({
        id: test.id,
        rowKey: `${test.id}:${override.id}`,
        location: `${slug}.override-${i + 1}`,
        fieldPath: 'answerOverrides',
        subId: override.id,
        textObj: override.text,
      })
    })
  }

  return rows
}

export const useLabTranslationStore = defineStore('labTranslation', {
  state: () => ({
    selectedLanguageCodes: [] as string[],
    onlyShowMissing: false,
    // Cells a fillInTheBlanks-only user has edited during this visit to the
    // translations section — lets them keep correcting a value they just
    // filled in without it re-locking the moment it's no longer blank.
    // Reset (not persisted) whenever the section itself is left.
    editedCellKeys: {} as Record<string, boolean>,
  }),

  getters: {
    hasActiveTranslationFilters(state): boolean {
      return state.selectedLanguageCodes.length > 0 || state.onlyShowMissing
    },

    isCellEditedThisVisit(state): (cellKey: string) => boolean {
      return (cellKey: string): boolean => !!state.editedCellKeys[cellKey]
    },

    analysisTranslations(): TranslationRow[] {
      const analysisStore = useAnalysisStore()
      const { currentLocale } = useAppStore()
      const seen: Record<string, number> = {}
      const rows = analysisStore.allAnalyses.map((analysis) => {
        const base = kebabCase(
          analysis.name[currentLocale] || analysis.name['en'] || ''
        )
        seen[base] = (seen[base] ?? 0) + 1
        const slug = seen[base] === 1 ? base : `${base}-${seen[base]}`
        return {
          id: analysis.id,
          rowKey: analysis.id,
          location: `${slug}.name`,
          fieldPath: 'name',
          textObj: analysis.name,
        }
      })
      return sortBy(rows, 'location')
    },

    sampleAreaTranslations(): TranslationRow[] {
      const sampleAreaStore = useSampleAreaStore()
      const { currentLocale } = useAppStore()
      const seen: Record<string, number> = {}
      const rows = sampleAreaStore.allSampleAreas.map((sampleArea) => {
        const base = kebabCase(
          sampleArea.name[currentLocale] || sampleArea.name['en'] || ''
        )
        seen[base] = (seen[base] ?? 0) + 1
        const slug = seen[base] === 1 ? base : `${base}-${seen[base]}`
        return {
          id: sampleArea.id,
          rowKey: sampleArea.id,
          location: `${slug}.name`,
          fieldPath: 'name',
          textObj: sampleArea.name,
        }
      })
      return sortBy(rows, 'location')
    },

    analysisTestTranslations(): TranslationRow[] {
      const analysisTestStore = useAnalysisTestStore()
      const { currentLocale } = useAppStore()
      const slugMap = buildSlugMap(
        analysisTestStore.allAnalysisTests,
        currentLocale
      )
      const sortedTests = sortBy(
        analysisTestStore.allAnalysisTests,
        (test) => slugMap[test.id] ?? ''
      )
      return sortedTests.flatMap((test) =>
        buildTestRows(test, slugMap[test.id] ?? '')
      )
    },

    tatTranslations(): TranslationRow[] {
      const turnaroundTimeStore = useTurnaroundTimeStore()
      const { currentLocale } = useAppStore()
      const walkInTats = turnaroundTimeStore.allTATs.filter(
        (tat) => tat.type === 'walk-in'
      )
      const seen: Record<string, number> = {}
      const rows = walkInTats.map((tat) => {
        const name = tat.name as Record<string, string>
        const base = kebabCase(name[currentLocale] || name['en'] || '')
        seen[base] = (seen[base] ?? 0) + 1
        const slug = seen[base] === 1 ? base : `${base}-${seen[base]}`
        return {
          id: tat.id,
          rowKey: tat.id,
          location: `${slug}.name`,
          fieldPath: 'name',
          textObj: name,
        }
      })
      return sortBy(rows, 'location')
    },

    addonServiceTranslations(): TranslationRow[] {
      const addonServiceStore = useAddonServiceStore()
      const { currentLocale } = useAppStore()
      const seen: Record<string, number> = {}
      const rows = addonServiceStore.allAddonServices.map((addonService) => {
        const name = addonService.name as Record<string, string>
        const base = kebabCase(name[currentLocale] || name['en'] || '')
        seen[base] = (seen[base] ?? 0) + 1
        const slug = seen[base] === 1 ? base : `${base}-${seen[base]}`
        return {
          id: addonService.id,
          rowKey: addonService.id,
          location: `${slug}.name`,
          fieldPath: 'name',
          textObj: name,
        }
      })
      return sortBy(rows, 'location')
    },

    labEquipmentTranslations(): TranslationRow[] {
      const labEquipmentStore = useLabEquipmentStore()
      const activeEquipment = labEquipmentStore.labEquipment.filter(
        (e) => e.activeState === 'active'
      )
      const rows: TranslationRow[] = []
      activeEquipment.forEach((equipment) => {
        const base = kebabCase(equipment.serialNumber || '')
        rows.push({
          id: equipment.id,
          rowKey: `${equipment.id}:name`,
          location: `${base}.name`,
          fieldPath: 'name',
          textObj: equipment.name,
        })
        rows.push({
          id: equipment.id,
          rowKey: `${equipment.id}:type`,
          location: `${base}.type`,
          fieldPath: 'type',
          textObj: equipment.type,
        })
      })
      return sortBy(rows, 'location')
    },

    pricingSegmentTranslations(): TranslationRow[] {
      const pricingSegmentStore = usePricingSegmentStore()
      const { currentLocale } = useAppStore()
      const activeSegments = pricingSegmentStore.allPricingSegments.filter(
        (s) => s.activeState === 'active'
      )
      const seen: Record<string, number> = {}
      const rows = activeSegments.map((pricingSegment) => {
        const base = kebabCase(
          pricingSegment.name[currentLocale] || pricingSegment.name['en'] || ''
        )
        seen[base] = (seen[base] ?? 0) + 1
        const slug = seen[base] === 1 ? base : `${base}-${seen[base]}`
        return {
          id: pricingSegment.id,
          rowKey: pricingSegment.id,
          location: `${slug}.name`,
          fieldPath: 'name',
          textObj: pricingSegment.name,
        }
      })
      return sortBy(rows, 'location')
    },

    closedDayTranslations(): TranslationRow[] {
      const closedDayStore = useClosedDayStore()
      const { currentLocale } = useAppStore()
      const seen: Record<string, number> = {}
      const rows = closedDayStore.allClosedDays.map((closedDay) => {
        const base = kebabCase(
          closedDay.name[currentLocale] || closedDay.name['en'] || ''
        )
        seen[base] = (seen[base] ?? 0) + 1
        const slug = seen[base] === 1 ? base : `${base}-${seen[base]}`
        return {
          id: closedDay.id,
          rowKey: closedDay.id,
          // The date disambiguates recurring names (e.g. the same holiday
          // every year) far better than the generic numeric suffix alone.
          location: `${slug} (${closedDay.date})`,
          fieldPath: 'name',
          textObj: closedDay.name,
        }
      })
      return sortBy(rows, 'location')
    },

    dropPointTranslations(): TranslationRow[] {
      const dropPointStore = useDropPointStore()
      const { currentLocale } = useAppStore()
      const activeLabDropPoints = dropPointStore.labDropPoints.filter(
        (p) => p.activeState === 'active'
      )
      const rows: TranslationRow[] = []
      activeLabDropPoints.forEach((dropPoint) => {
        const base = kebabCase(
          dropPoint.name[currentLocale] || dropPoint.name['en'] || ''
        )
        rows.push({
          id: dropPoint.id,
          rowKey: `${dropPoint.id}:name`,
          location: `${base}.name`,
          fieldPath: 'name',
          textObj: dropPoint.name,
        })
        rows.push({
          id: dropPoint.id,
          rowKey: `${dropPoint.id}:placement`,
          location: `${base}.placement`,
          fieldPath: 'placement',
          textObj: dropPoint.placement,
        })
      })
      return sortBy(rows, 'location')
    },

    reportDescriptionTranslations(): TranslationRow[] {
      const reportDescriptionStore = useReportDescriptionStore()
      const { currentLocale } = useAppStore()
      const activeReportDescriptions =
        reportDescriptionStore.allReportDescriptions.filter(
          (r) => r.activeState === 'active'
        )
      const seen: Record<string, number> = {}
      const rows = activeReportDescriptions.map((reportDescription) => {
        const base = kebabCase(
          reportDescription.name[currentLocale] ||
            reportDescription.name['en'] ||
            ''
        )
        seen[base] = (seen[base] ?? 0) + 1
        const slug = seen[base] === 1 ? base : `${base}-${seen[base]}`
        return {
          id: reportDescription.id,
          rowKey: reportDescription.id,
          location: `${slug}.name`,
          fieldPath: 'name',
          textObj: reportDescription.name,
        }
      })
      return sortBy(rows, 'location')
    },

    textListItemTranslations(): (listID: string) => TranslationRow[] {
      const textListStore = useTextListStore()
      const { currentLocale } = useAppStore()
      return (listID: string): TranslationRow[] => {
        const list = textListStore.allTextLists.find(
          (l) => l.id === listID || l.systemKey === listID
        )
        if (!list) return []
        const activeEntries = Object.entries(list.items).filter(
          ([, item]) => item.activeState === 'active'
        )
        return sortBy(
          activeEntries,
          ([, item]) =>
            item.text[currentLocale] ??
            Object.values(item.text).find((v) => v !== '') ??
            ''
        ).map(([itemID, item]) => ({
          id: itemID,
          rowKey: itemID,
          fieldPath: 'items',
          label:
            item.text[currentLocale] ??
            Object.values(item.text).find((v) => v !== '') ??
            '',
          textObj: item.text,
        }))
      }
    },
  },

  actions: {
    setSelectedLanguageCodes(codes: string[]): void {
      this.selectedLanguageCodes = codes
    },

    setOnlyShowMissing(value: boolean): void {
      this.onlyShowMissing = value
    },

    clearTranslationFilters(): void {
      this.selectedLanguageCodes = []
      this.onlyShowMissing = false
    },

    markCellEdited(cellKey: string): void {
      this.editedCellKeys[cellKey] = true
    },

    resetEditedCellKeys(): void {
      this.editedCellKeys = {}
    },

    async saveAnalysisTranslation(
      analysisID: string,
      locale: string,
      value: string
    ): Promise<void> {
      const docRef = doc(db, 'Apps', '-lab-management-', 'Analyses', analysisID)
      await updateDoc(docRef, {
        [`name.${locale}`]: value,
        'timestamps.updatedAt': Date.now(),
      })
    },

    async saveSampleAreaTranslation(
      sampleAreaID: string,
      locale: string,
      value: string
    ): Promise<void> {
      const docRef = doc(
        db,
        'Apps',
        '-lab-management-',
        'SampleAreas',
        sampleAreaID
      )
      await updateDoc(docRef, {
        [`name.${locale}`]: value,
        'timestamps.updatedAt': Date.now(),
      })
    },

    async saveAnalysisTestTranslation(
      analysisTestID: string,
      fieldPath: string,
      subId: string | undefined,
      locale: string,
      value: string
    ): Promise<void> {
      const docRef = doc(
        db,
        'Apps',
        '-lab-management-',
        'AnalysisTests',
        analysisTestID
      )
      if (fieldPath === 'name') {
        await updateDoc(docRef, {
          [`name.${locale}`]: value,
          'timestamps.updatedAt': Date.now(),
        })
        return
      }
      if (fieldPath === 'displayName') {
        await updateDoc(docRef, {
          [`displayName.${locale}`]: value,
          'timestamps.updatedAt': Date.now(),
        })
        return
      }
      const analysisTestStore = useAnalysisTestStore()
      const test = analysisTestStore.allAnalysisTests.find(
        (t) => t.id === analysisTestID
      )
      if (!test) return
      if (fieldPath === 'answers') {
        const updatedAnswers = test.answers.map((a) =>
          a.id === subId ? { ...a, text: { ...a.text, [locale]: value } } : a
        )
        await updateDoc(docRef, {
          answers: updatedAnswers,
          'timestamps.updatedAt': Date.now(),
        })
      } else if (fieldPath === 'answerOverrides') {
        const updatedOverrides = test.answerOverrides.map((o) =>
          o.id === subId ? { ...o, text: { ...o.text, [locale]: value } } : o
        )
        await updateDoc(docRef, {
          answerOverrides: updatedOverrides,
          'timestamps.updatedAt': Date.now(),
        })
      }
    },

    async saveTatTranslation(
      tatID: string,
      locale: string,
      value: string
    ): Promise<void> {
      const docRef = doc(db, 'Apps', '-lab-management-', 'TATs', tatID)
      await updateDoc(docRef, {
        [`name.${locale}`]: value,
        'timestamps.updatedAt': Date.now(),
      })
    },

    async saveAddonServiceTranslation(
      addonServiceID: string,
      locale: string,
      value: string
    ): Promise<void> {
      const docRef = doc(
        db,
        'Apps',
        '-lab-management-',
        'AddonServices',
        addonServiceID
      )
      await updateDoc(docRef, {
        [`name.${locale}`]: value,
        'timestamps.updatedAt': Date.now(),
      })
    },

    async saveLabEquipmentTranslation(
      equipmentID: string,
      fieldPath: string,
      locale: string,
      value: string
    ): Promise<void> {
      const docRef = doc(
        db,
        'Apps',
        '-lab-management-',
        'LabEquipment',
        equipmentID
      )
      await updateDoc(docRef, {
        [`${fieldPath}.${locale}`]: value,
        'timestamps.updatedAt': Date.now(),
      })
    },

    async saveTextListItemTranslation(
      listID: string,
      itemID: string,
      locale: string,
      value: string
    ): Promise<void> {
      const textListStore = useTextListStore()
      const docID =
        textListStore.allTextLists.find((l) => l.systemKey === listID)?.id ??
        listID
      const docRef = doc(db, 'Apps', '-lab-management-', 'TextLists', docID)
      await updateDoc(docRef, {
        [`items.${itemID}.text.${locale}`]: value,
        'timestamps.updatedAt': Date.now(),
      })
    },

    async savePricingSegmentTranslation(
      segmentID: string,
      locale: string,
      value: string
    ): Promise<void> {
      const docRef = doc(
        db,
        'Apps',
        '-lab-management-',
        'PricingSegments',
        segmentID
      )
      await updateDoc(docRef, {
        [`name.${locale}`]: value,
        'timestamps.updatedAt': Date.now(),
      })
    },

    async saveClosedDayTranslation(
      closedDayID: string,
      locale: string,
      value: string
    ): Promise<void> {
      const docRef = doc(
        db,
        'Apps',
        '-lab-management-',
        'ClosedDays',
        closedDayID
      )
      // ClosedDay has no timestamps.updatedAt field (see ClosedDay.types.ts) —
      // unlike the other translation targets, so it is not written here.
      await updateDoc(docRef, { [`name.${locale}`]: value })
    },

    async saveDropPointTranslation(
      dropPointID: string,
      fieldPath: string,
      locale: string,
      value: string
    ): Promise<void> {
      const docRef = doc(
        db,
        'Apps',
        '-lab-management-',
        'LabDropPoints',
        dropPointID
      )
      await updateDoc(docRef, {
        [`${fieldPath}.${locale}`]: value,
        'timestamps.updatedAt': Date.now(),
      })
    },

    async saveReportDescriptionTranslation(
      reportDescriptionID: string,
      fieldPath: string,
      locale: string,
      value: string
    ): Promise<void> {
      const docRef = doc(
        db,
        'Apps',
        '-lab-management-',
        'ReportDescriptions',
        reportDescriptionID
      )
      await updateDoc(docRef, {
        [`${fieldPath}.${locale}`]: value,
        'timestamps.updatedAt': Date.now(),
      })
    },

  },
})

